import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  MessageCircle,
  Ticket,
  Timer,
  Bookmark,
  Calendar,
  Play,
  Clock,
  Search,
  Loader2,
  X,
  Square,
  Euro,
  ChevronDown,
  Bug,
  FolderPlus
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import api from '../../lib/axios';
import { toast } from 'sonner';

// =============================================
// FLOATING ACTION BUTTON (FAB) WIDGET
// Style inspiré de provencepaysage.fr
// Déploie des actions rapides vers le haut
// Avec Chrono Rapide intégré
// =============================================

const QuickActionsWidget = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingTickets, setPendingTickets] = useState(0);
  const [activeTimersCount, setActiveTimersCount] = useState(0);
  const [activeTimers, setActiveTimers] = useState([]);
  const menuRef = useRef(null);
  
  // Quick Timer Modal State
  const [showQuickTimer, setShowQuickTimer] = useState(false);
  const [showActiveTimers, setShowActiveTimers] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [startingTimer, setStartingTimer] = useState(null);
  const [stoppingTimer, setStoppingTimer] = useState(null);
  
  // Check if we're on the chat page to adjust position
  const isOnChatPage = location.pathname === '/chat';

  // Fetch unread counts and active timers
  const fetchCounts = useCallback(async () => {
    if (!user || user.role === 'client') return;
    
    try {
      const convRes = await api.get('/internal-chat/conversations');
      const convs = convRes.data || [];
      const totalUnread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadMessages(totalUnread);
    } catch (e) { /* ignore */ }
    
    try {
      const ticketRes = await api.get('/tickets');
      const tickets = ticketRes.data || [];
      const pending = tickets.filter(t => t.status === 'open' || t.status === 'pending').length;
      setPendingTickets(pending);
    } catch (e) { /* ignore */ }
    
    try {
      const timerRes = await api.get('/time/active-timers');
      const timers = timerRes.data || [];
      setActiveTimers(timers);
      setActiveTimersCount(timers.length);
    } catch (e) { /* ignore */ }
  }, [user]);

  // Stop a timer
  const handleStopTimer = async (timerId) => {
    setStoppingTimer(timerId);
    try {
      await api.post(`/time/${timerId}/stop`);
      toast.success('Chrono arrêté');
      fetchCounts();
    } catch (error) {
      toast.error('Erreur lors de l\'arrêt du chrono');
    } finally {
      setStoppingTimer(null);
    }
  };

  // Calculate total time and gains
  const calculateTotals = () => {
    let totalMinutes = 0;
    let totalGains = 0;
    
    activeTimers.forEach(timer => {
      const minutes = timer.duration_minutes || 0;
      totalMinutes += minutes;
      const hours = minutes / 60;
      const rate = timer.hourly_rate || 0;
      totalGains += hours * rate;
    });
    
    return { totalMinutes, totalGains };
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}h ${mins.toString().padStart(2, '0')}m`;
  };

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Fetch tasks for Quick Timer modal
  const fetchTasksForTimer = async () => {
    setLoadingTasks(true);
    try {
      const res = await api.get('/tasks/');
      // Filter only tasks that are not completed and belong to current user or are unassigned
      const availableTasks = (res.data || []).filter(t => 
        t.status !== 'completed' && t.status !== 'cancelled' && t.status !== 'validated'
      );
      setTasks(availableTasks);
    } catch (e) {
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoadingTasks(false);
    }
  };

  // Start timer for a task
  const handleStartTimer = async (task) => {
    setStartingTimer(task.id);
    try {
      await api.post('/time/start', {
        task_id: task.id,
        project_id: task.project_id,
        description: task.name || task.title
      });
      toast.success(`Chrono démarré pour: ${task.name || task.title}`);
      setShowQuickTimer(false);
      setActiveTimersCount(prev => prev + 1);
      fetchCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du démarrage du chrono');
    } finally {
      setStartingTimer(null);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Don't render for clients or affiliates
  if (!user || user.role === 'client' || user.role === 'affiliate') return null;

  const accentColor = theme?.colors?.accent || '#00d4aa';
  const primaryColor = theme?.colors?.primary || '#1a365d';

  // Menu items configuration
  const menuItems = [
    {
      id: 'new-project',
      icon: FolderPlus,
      label: 'Nouveau Projet',
      color: '#6366F1', // Indigo
      badge: 0,
      onClick: () => navigate('/agency/new-project')
    },
    {
      id: 'quick-timer',
      icon: Play,
      label: 'Chrono Rapide',
      color: '#10B981', // Emerald green
      badge: 0,
      onClick: () => {
        setShowQuickTimer(true);
        fetchTasksForTimer();
      }
    },
    {
      id: 'chat',
      icon: MessageCircle,
      label: 'Chat',
      color: '#25D366', // WhatsApp green
      badge: unreadMessages,
      onClick: () => navigate('/chat')
    },
    {
      id: 'tickets',
      icon: Ticket,
      label: 'Tickets',
      color: '#FF6B35', // Orange
      badge: pendingTickets,
      onClick: () => navigate('/tickets')
    },
    {
      id: 'chrono',
      icon: Timer,
      label: 'Mes Chronos',
      color: '#4F46E5', // Indigo
      badge: activeTimersCount,
      onClick: () => {
        if (activeTimersCount > 0) {
          setShowActiveTimers(true);
        } else {
          navigate('/time-tracking');
        }
      }
    },
    {
      id: 'links',
      icon: Bookmark,
      label: 'Mémos',
      color: '#EC4899', // Pink
      onClick: () => navigate('/saved-links')
    },
    {
      id: 'calendar',
      icon: Calendar,
      label: 'Agenda',
      color: '#0EA5E9', // Sky blue
      onClick: () => navigate('/calendar')
    },
    {
      id: 'bug-report',
      icon: Bug,
      label: 'Signaler un bug',
      color: '#EF4444', // Red
      onClick: () => {
        // Trigger the bug report dialog
        const bugButton = document.querySelector('[data-testid="bug-report-button"]');
        if (bugButton) {
          bugButton.click();
        }
      }
    }
  ];

  const totalBadge = unreadMessages + pendingTickets + activeTimersCount;
  
  // Filter tasks based on search
  const filteredTasks = tasks.filter(t => 
    (t.name || t.title || '').toLowerCase().includes(taskSearch.toLowerCase()) ||
    (t.project_name || '').toLowerCase().includes(taskSearch.toLowerCase())
  );

  return (
    <>
      <div 
        ref={menuRef}
        data-testid="floating-widget-container"
        className={isOnChatPage ? 'fab-on-chat' : 'fab-default'}
        style={{
          position: 'fixed',
          // On chat page, position higher to avoid overlapping the input
          bottom: isOnChatPage ? '100px' : '24px',
          right: '24px',
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '12px',
          // CRITICAL: Allow clicks to pass through the container to elements below
          pointerEvents: 'none'
        }}
      >
        {/* Menu Items - Appear when open */}
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '12px',
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: isOpen ? 'auto' : 'none'
          }}
        >
          {menuItems.map((item, index) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? 'translateX(0)' : 'translateX(20px)',
                transition: `all 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s`
              }}
            >
              {/* Label */}
              <span
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '8px 16px',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.label}
              </span>
              
              {/* Icon Button */}
              <button
                data-testid={`fab-${item.id}`}
                onClick={() => {
                  setIsOpen(false);
                  item.onClick();
                }}
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                }}
              >
                <item.icon size={24} />
                
                {/* Badge */}
                {item.badge > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      minWidth: '22px',
                      height: '22px',
                      borderRadius: '11px',
                      backgroundColor: '#EF4444',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 6px',
                      border: '2px solid white'
                    }}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Main FAB Button */}
        <button
          data-testid="fab-main-button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: isOpen ? '#EF4444' : accentColor,
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
            // Re-enable pointer events for the button itself
            pointerEvents: 'auto'
          }}
          onMouseEnter={(e) => {
            if (!isOpen) {
              e.currentTarget.style.transform = 'scale(1.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <div
            style={{
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Plus size={28} strokeWidth={2.5} />
          </div>
          
          {/* Total Badge (when closed) */}
          {!isOpen && totalBadge > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                minWidth: '24px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: '#EF4444',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 6px',
                border: '2px solid white',
                animation: 'pulse 2s infinite'
              }}
            >
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          )}
        </button>

        {/* CSS Animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          
          /* Mobile styles */
          @media (max-width: 768px) {
            .fab-default {
              bottom: 16px !important;
              right: 16px !important;
            }
            .fab-on-chat {
              bottom: 100px !important;
              right: 16px !important;
            }
          }
        `}</style>
      </div>

      {/* Quick Timer Modal */}
      <Dialog open={showQuickTimer} onOpenChange={setShowQuickTimer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" style={{ color: accentColor }} />
              Chrono Rapide
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une tâche..."
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Tasks List */}
            <ScrollArea className="h-[300px] border rounded-lg">
              {loadingTasks ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500 p-4">
                  <Clock className="w-12 h-12 mb-2 opacity-30" />
                  <p className="text-sm">Aucune tâche disponible</p>
                  <Button 
                    variant="link" 
                    size="sm"
                    onClick={() => {
                      setShowQuickTimer(false);
                      navigate('/tasks');
                    }}
                  >
                    Créer une tâche
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate" style={{ color: theme.colors?.primary }}>
                          {task.name || task.title}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {task.project_name || 'Sans projet'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleStartTimer(task)}
                        disabled={startingTimer === task.id}
                        style={{ backgroundColor: accentColor }}
                        className="text-white shrink-0"
                      >
                        {startingTimer === task.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-1" />
                            Démarrer
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {/* Quick action to go to time tracking */}
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-xs text-slate-500">
                {activeTimersCount > 0 && `${activeTimersCount} chrono(s) actif(s)`}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowQuickTimer(false);
                  navigate('/time-tracking');
                }}
              >
                <Timer className="w-4 h-4 mr-1" />
                Voir tous les chronos
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Timers Modal */}
      <Dialog open={showActiveTimers} onOpenChange={setShowActiveTimers}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-indigo-600" />
              Chronos actifs ({activeTimersCount})
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            {/* Total Summary */}
            {activeTimers.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-indigo-700">
                    {formatDuration(calculateTotals().totalMinutes)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Euro className="w-4 h-4 text-emerald-600" />
                  <span className="font-bold text-emerald-600">
                    {calculateTotals().totalGains.toFixed(2)}€
                  </span>
                </div>
              </div>
            )}
            
            {/* Active Timers List */}
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-2">
                {activeTimers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                    <Timer className="w-12 h-12 mb-2 opacity-30" />
                    <p className="text-sm">Aucun chrono actif</p>
                    <Button 
                      variant="link" 
                      size="sm"
                      onClick={() => {
                        setShowActiveTimers(false);
                        setShowQuickTimer(true);
                        fetchTasksForTimer();
                      }}
                    >
                      Démarrer un chrono
                    </Button>
                  </div>
                ) : (
                  activeTimers.map((timer) => {
                    const minutes = timer.duration_minutes || 0;
                    const hours = minutes / 60;
                    const gain = hours * (timer.hourly_rate || 0);
                    
                    return (
                      <div
                        key={timer.id}
                        className="flex items-center gap-3 p-3 bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-100 hover:border-slate-200 transition-all"
                      >
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Timer className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate" style={{ color: theme.colors?.primary }}>
                            {timer.task_name || timer.description || 'Sans titre'}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {timer.project_name || 'Sans projet'}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-indigo-600 font-mono">
                            {formatDuration(minutes)}
                          </p>
                          {timer.hourly_rate > 0 && (
                            <p className="text-xs text-emerald-600 font-medium">
                              +{gain.toFixed(2)}€
                            </p>
                          )}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleStopTimer(timer.id)}
                          disabled={stoppingTimer === timer.id}
                          className="shrink-0 h-8 w-8 rounded-lg hover:bg-red-50 hover:text-red-600"
                        >
                          {stoppingTimer === timer.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
            
            {/* Actions */}
            <div className="flex justify-between items-center pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowActiveTimers(false);
                  setShowQuickTimer(true);
                  fetchTasksForTimer();
                }}
              >
                <Play className="w-4 h-4 mr-1" />
                Nouveau chrono
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowActiveTimers(false);
                  navigate('/time-tracking');
                }}
              >
                <Timer className="w-4 h-4 mr-1" />
                Historique
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickActionsWidget;
